import React from "react";
import { Typography } from "./Typography";
import BrahmaLogoWithNameIcon from "../icons/BrahmaLogoWithNameIcon";
import USDCIcon from "../icons/USDCIcon";
import VisaIcon from "../icons/VisaIcon";
import USDT0Icon from "../icons/USDT0Icon";
import {
  CreditProviderId,
  isCreditProviderOfType,
} from "../../config/creditRegistry";
import { useCreditData } from "../../hooks/useCreditData";
import { getVaultTokenIcon } from "../../api/utils";
import { getCreditProvider } from "../../config/creditRegistry";

type CreditCardProps = {
  number: string;
  limit: number;
  showPin?: boolean;
  expiry?: string;
  cvv?: string;
  onToggleViewPin?: () => void;
  isLoading?: boolean;
  variant?: "fixed" | "dynamic";
  width?: string;
  height?: string;
  solverType?: CreditProviderId;
};

export function CreditCard({
  number,
  limit,
  showPin = false,
  onToggleViewPin,
  expiry,
  cvv,
  isLoading,
  variant = "fixed",
  width,
  height,
  solverType,
}: CreditCardProps) {
  const getCardDimensions = () => {
    if (variant === "dynamic" && width && height) {
      return `${width} ${height}`;
    }
    return "w-full h-[230px] sm:h-[260px] md:h-[262px] lg:h-[290px]";
  };

  const isSpendModeType = solverType
    ? isCreditProviderOfType(solverType, "spend")
    : false;

  const { creditInfo } = useCreditData();
  let spendModeIcon = null;

  if (isSpendModeType && solverType) {
    const provider = getCreditProvider(solverType);
    const creditData = provider ? creditInfo[provider.storeKey] : null;
    const metadata = creditData?.data?.metadata as any;

    let underlyingAsset: string | null = null;
    if (Array.isArray(metadata) && metadata[0]?.underlyingAsset) {
      underlyingAsset = metadata[0]?.underlyingAsset;
    } else if (metadata?.suppliedAssets?.[0]?.underlyingAsset) {
      underlyingAsset = metadata.suppliedAssets[0]?.underlyingAsset;
    }

    if (underlyingAsset) {
      const vaultTokens = getVaultTokenIcon(26);
      const matchingToken = Object.entries(vaultTokens).find(
        ([address]) => address.toLowerCase() === underlyingAsset?.toLowerCase()
      );
      if (matchingToken) {
        spendModeIcon = matchingToken[1].icon;
      }
    }
  }

  return (
    <div
      className={`${getCardDimensions()} overflow-hidden relative rounded-[4px] sm:rounded-[4px] border-2 border-white sm:border-2 sm:border-white bg-[#E2DBC2] shadow-[inset_-0.828px_-0.828px_0_1.656px_#0A0A0A,inset_0.828px_0.828px_0_1.656px_#DFDFDF,inset_-1.656px_-1.656px_0_1.656px_#808080,inset_1.656px_1.656px_0_1.656px_#FFF] flex flex-col p-[4px]`}
    >
      <div className="bg-[#0C4F3D] px-[6px] py-[4px]">
        <Typography className="text-[#FFF] text-[16px] leading-[16px] font-[600]">
          Brahma Virtual Card
        </Typography>
      </div>
      {/* {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-10">
          <Loader gifPath="/merlingLoading.gif" />
        </div>
      )} */}
      <div className="flex flex-col gap-1 lg:gap-3 p-2 lg:p-4 h-full overflow-hidden">
        <div className="flex flex-col">
          <div className="flex justify-between items-center">
            <Typography className="text-[#020202] text-[16px] sm:text-[22.773px] font-medium leading-normal font-mono tracking-widest">
              {number}
            </Typography>

            {onToggleViewPin && (
              <div
                className={`ml-2 ${
                  isLoading ? "cursor-wait" : "cursor-pointer"
                }`}
                onClick={onToggleViewPin}
              >
                {isLoading ? (
                  <div className="animate-bounce">
                    <EyeIcon />
                  </div>
                ) : showPin ? (
                  <EyeOffIcon />
                ) : (
                  <EyeIcon />
                )}
              </div>
            )}
          </div>

          {/* expiry/cvv Section */}
          {(expiry || cvv) && (
            <div className="flex items-center gap-3 sm:gap-5 mt-2">
              <Typography className="text-[#020202] text-[16px] sm:text-[22.773px] font-mono tracking-widest">
                {expiry}
              </Typography>
              <Typography className="text-[#020202] text-[16px] sm:text-[22.773px] font-mono tracking-widest">
                {cvv}
              </Typography>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Typography className="text-[#020202] text-[10px] sm:text-[12.773px] font-medium leading-normal tracking-[-0.751px]">
            Credit Limit
          </Typography>

          <div className="border-[0.67px] sm:border-[0.957px] border-[#808080] bg-[#F2E6BE] shadow-[inset_-0.335px_-0.335px_0px_0.67px_#0A0A0A,inset_0.335px_0.335px_0px_0.67px_#DFDFDF,inset_-0.67px_-0.67px_0px_0.67px_#808080,inset_0.67px_0.67px_0px_0.67px_#FFF] sm:shadow-[inset_-0.478px_-0.478px_0px_0.957px_#0A0A0A,inset_0.478px_0.478px_0px_0.957px_#DFDFDF,inset_-0.957px_-0.957px_0px_0.957px_#808080,inset_0.957px_0.957px_0px_0.957px_#FFF] flex w-full p-[6.13px_4.6px] sm:p-[8.76px_6.57px] justify-between items-center px-2">
            <div className="flex items-center gap-2">
              {isSpendModeType && spendModeIcon ? (
                spendModeIcon
              ) : (
                <USDCIcon width={26} height={26} />
              )}
              <Typography className="text-[#020202] text-[11.159px] sm:text-[15.941px] font-medium leading-normal tracking-[-0.751px]">
                ${limit}
              </Typography>
            </div>
            <Typography className="text-[#020202] text-[10.5px] sm:text-[15px] font-medium leading-normal tracking-[-0.751px]">
              ${limit}
            </Typography>
          </div>
        </div>

        <div className="flex flex-col gap-1 mt-auto">
          <Typography className="text-[#020202] text-[10px] sm:text-[12.773px] font-medium leading-normal tracking-[-0.751px]">
            Powered by
          </Typography>

          <div className="hidden sm:flex justify-between items-center">
            <BrahmaLogoWithNameIcon width={116} height={18} />
            <VisaIcon width={71} height={32} />
          </div>
          <div className="flex sm:hidden justify-between items-center">
            <BrahmaLogoWithNameIcon width={63} height={10} />
            <VisaIcon width={39} height={18} />
          </div>
        </div>
      </div>
    </div>
  );
}

const EyeIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="25"
      height="15"
      viewBox="0 0 25 15"
      fill="none"
    >
      <path
        d="M8.41237 0.0390625H16.6905V2.10859H8.41237V0.0390625ZM4.27332 4.17811V2.10859H8.41237V4.17811H4.27332ZM2.2038 6.24763V4.17811H4.27332V6.24763H2.2038ZM2.2038 8.31716V6.24763H0.134277V8.31716H2.2038ZM4.27332 10.3867H2.2038V8.31716H4.27332V10.3867ZM8.41237 12.4562H4.27332V10.3867H8.41237V12.4562ZM16.6905 12.4562V14.5257H8.41237V12.4562H16.6905ZM20.8295 10.3867V12.4562H16.6905V10.3867H20.8295ZM22.899 8.31716V10.3867H20.8295V8.31716H22.899ZM22.899 6.24763H24.9686V8.31716H22.899V6.24763ZM20.8295 4.17811H22.899V6.24763H20.8295V4.17811ZM20.8295 4.17811V2.10859H16.6905V4.17811H20.8295ZM10.4819 5.21287H14.6209V9.35192H10.4819V5.21287Z"
        fill="black"
      />
    </svg>
  );
};

const EyeOffIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="25"
    height="15"
    viewBox="0 0 25 15"
    fill="none"
  >
    <path d="M2 2L23 13" stroke="black" strokeWidth="2" />
    <path
      d="M8.41237 0.0390625H16.6905V2.10859H8.41237V0.0390625ZM4.27332 4.17811V2.10859H8.41237V4.17811H4.27332ZM2.2038 6.24763V4.17811H4.27332V6.24763H2.2038ZM2.2038 8.31716V6.24763H0.134277V8.31716H2.2038ZM4.27332 10.3867H2.2038V8.31716H4.27332V10.3867ZM8.41237 12.4562H4.27332V10.3867H8.41237V12.4562ZM16.6905 12.4562V14.5257H8.41237V12.4562H16.6905ZM20.8295 10.3867V12.4562H16.6905V10.3867H20.8295ZM22.899 8.31716V10.3867H20.8295V8.31716H22.899ZM22.899 6.24763H24.9686V8.31716H22.899V6.24763ZM20.8295 4.17811H22.899V6.24763H20.8295V4.17811ZM20.8295 4.17811V2.10859H16.6905V4.17811H20.8295ZM10.4819 5.21287H14.6209V9.35192H10.4819V5.21287Z"
      fill="black"
    />
  </svg>
);
